export type ReasonType = "unknown" | "uncertain";

export interface ReasonInfo {
  id: string;
  name: string;
  description: string;
  type: ReasonType;
}

export const REASON_INFO: Record<string, ReasonInfo> = {
  PreviouslyUnknown: {
    id: "PreviouslyUnknown",
    name: "Previously Unknown",
    description: "The value remained unknown from a prior resolution step.",
    type: "unknown",
  },
  MissingInput: { id: "MissingInput", name: "Missing Input", description: "No input was provided.", type: "unknown" },
  RequiresUnresolved: {
    id: "RequiresUnresolved",
    name: "Requires Unknown",
    description: "This attribute depends on another attribute that is also unknown.",
    type: "unknown",
  },
  Unknown: {
    id: "Unknown",
    name: "Not Processed",
    description: "The attribute was not processed yet. This can be because either the attribute is not connected to the goal or it's value is not required during the solve.",
    type: "unknown",
  },
  UnknownInstanceCount: {
    id: "UnknownInstanceCount",
    name: "Unknown Instance Count",
    description: "The engine could not determine how many instances exist for an entity.",
    type: "unknown",
  },
  ResolveLoop: {
    id: "ResolveLoop",
    name: "Solve Loop",
    description: "A circular dependency was detected during solving.",
    type: "unknown",
  },
  TriggerReset: {
    id: "TriggerReset",
    name: "Trigger Reset",
    description: "The trigger associated with this attribute was reset.",
    type: "unknown",
  },
  RelationshipUnknown: {
    id: "RelationshipUnknown",
    name: "Relationship Unknown",
    description: "The relationship was unknown.",
    type: "unknown",
  },

  Uncertain: {
    id: "Uncertain",
    name: "Uncertain",
    description: "The engine produced an uncertain value.",
    type: "uncertain",
  },
  TimeoutError: {
    id: "TimeoutError",
    name: "Timeout Error",
    description: "Solve did not complete before the engine timeout.",
    type: "uncertain",
  },
  Unimplemented: { id: "Unimplemented", name: "Unimplemented", description: "Unimplemented.", type: "uncertain" },
  NotEnoughValues: {
    id: "NotEnoughValues",
    name: "Not Enough Values",
    description: "There were more rows and values.",
    type: "uncertain",
  },
  ExpectedSingleValue: {
    id: "ExpectedSingleValue",
    name: "Expected Single Value",
    description: "The operation expected exactly one value.",
    type: "uncertain",
  },
  ExpectedMultipleValues: {
    id: "ExpectedMultipleValues",
    name: "Expected Multiple Values",
    description: "The operation expected multiple values.",
    type: "uncertain",
  },
  InvalidResolve: {
    id: "InvalidResolve",
    name: "Invalid Resolve",
    description: "A Resolve element was misconfigured or invalid.",
    type: "uncertain",
  },
  MissingElementType: {
    id: "MissingElementType",
    name: "Missing Element Type",
    description: "An element did not specify a required type.",
    type: "uncertain",
  },
  ResolveGraphError: {
    id: "ResolveGraphError",
    name: "Solve Graph Error",
    description: "A lower-level graph solve error occurred.",
    type: "uncertain",
  },
  MultipleConditionsWithoutRows: {
    id: "MultipleConditionsWithoutRows",
    name: "Multiple Conditions Without Rows",
    description: "Multiple conditions were provided without matching row definitions.",
    type: "uncertain",
  },
  ExpectedTrue: {
    id: "ExpectedTrue",
    name: "Expected True",
    description: "A condition required a true value but got something else.",
    type: "uncertain",
  },
  ExpectedNonGlobalScope: {
    id: "ExpectedNonGlobalScope",
    name: "Expected Non-Global Scope",
    description: "A non-global scope was expected for this operation.",
    type: "uncertain",
  },
  AttributeNotFound: {
    id: "AttributeNotFound",
    name: "Attribute Not Found",
    description: "The referenced attribute could not be found in the release graph.",
    type: "uncertain",
  },
  NoEntitiesMatchRelationship: {
    id: "NoEntitiesMatchRelationship",
    name: "No Entities Match Relationship",
    description: "No entities matched the specified relationship scope.",
    type: "uncertain",
  },
  ExpectedNumber: {
    id: "ExpectedNumber",
    name: "Expected Number",
    description: "A numeric value was expected.",
    type: "uncertain",
  },
  ExpectedString: {
    id: "ExpectedString",
    name: "Expected String",
    description: "A string value was expected.",
    type: "uncertain",
  },
  ExpectedStringLike: {
    id: "ExpectedStringLike",
    name: "Expected String-Like",
    description: "A string-like value was expected for this operation.",
    type: "uncertain",
  },
  ExpectedDayOfWeek: {
    id: "ExpectedDayOfWeek",
    name: "Expected Day Of Week",
    description: "A valid day-of-week value was expected.",
    type: "uncertain",
  },
  ExpectedDateTime: {
    id: "ExpectedDateTime",
    name: "Expected Date Time",
    description: "A valid date/time value was expected.",
    type: "uncertain",
  },
  ExpectedPositiveNumber: {
    id: "ExpectedPositiveNumber",
    name: "Expected Positive Number",
    description: "A positive numeric value was expected.",
    type: "uncertain",
  },
  ExpectedTemporal: {
    id: "ExpectedTemporal",
    name: "Expected Temporal",
    description: "A temporal value was expected.",
    type: "uncertain",
  },
  ExpectedBoolean: {
    id: "ExpectedBoolean",
    name: "Expected Boolean",
    description: "A boolean value was expected.",
    type: "uncertain",
  },
  ExpectedTime: {
    id: "ExpectedTime",
    name: "Expected Time",
    description: "A valid time value was expected.",
    type: "uncertain",
  },
  NoMoreInstances: {
    id: "NoMoreInstances",
    name: "No More Instances",
    description: "There were no more instances to be inferred.",
    type: "uncertain",
  },
  ExpectedDateTimeSpan: {
    id: "ExpectedDateTimeSpan",
    name: "Expected Date Time Span",
    description: "A valid date/time span was expected.",
    type: "uncertain",
  },
  InvalidDateTimePart: {
    id: "InvalidDateTimePart",
    name: "Invalid Date Time Part",
    description: "An invalid date/time component was requested.",
    type: "uncertain",
  },
  FromGreaterThanTo: {
    id: "FromGreaterThanTo",
    name: "From Greater Than To",
    description: "A range start was greater than its end.",
    type: "uncertain",
  },
  DateTimeParseError: {
    id: "DateTimeParseError",
    name: "Date Time Parse Error",
    description: "A date/time value could not be parsed.",
    type: "uncertain",
  },
  TooFewElements: {
    id: "TooFewElements",
    name: "Too Few Elements",
    description: "The expression did not contain enough elements/arguments.",
    type: "uncertain",
  },
  TooFewInstances: {
    id: "TooFewInstances",
    name: "Too Few Instances",
    description: "Not enough entity instances were available.",
    type: "uncertain",
  },
  RegexCompileError: {
    id: "RegexCompileError",
    name: "Regex Compile Error",
    description: "A regular expression could not be compiled.",
    type: "uncertain",
  },
  ExpectedNegatable: {
    id: "ExpectedNegatable",
    name: "Expected Negatable",
    description: "A negatable value/expression was expected.",
    type: "uncertain",
  },
  ExpectedStringOrNumber: {
    id: "ExpectedStringOrNumber",
    name: "Expected String Or Number",
    description: "A string or number value was expected.",
    type: "uncertain",
  },
  ExpectedFileReferences: {
    id: "ExpectedFileReferences",
    name: "Expected File References",
    description: "File reference data was expected.",
    type: "uncertain",
  },
  ExpectedScalar: {
    id: "ExpectedScalar",
    name: "Expected Scalar",
    description: "A scalar value was expected.",
    type: "uncertain",
  },
  InstanceCountMismatch: {
    id: "InstanceCountMismatch",
    name: "Instance Count Mismatch",
    description: "Two related instance sets had incompatible counts.",
    type: "uncertain",
  },
  InvalidScope: {
    id: "InvalidScope",
    name: "Invalid Scope",
    description: "The provided entity scope was invalid for this operation.",
    type: "uncertain",
  },
  ExpectedUntil: {
    id: "ExpectedUntil",
    name: "Expected Until",
    description: "An Until clause/value was expected.",
    type: "uncertain",
  },
  ExpectedAttribute: {
    id: "ExpectedAttribute",
    name: "Expected Attribute",
    description: "A valid attribute reference was expected.",
    type: "uncertain",
  },
  NodeNotExists: {
    id: "NodeNotExists",
    name: "Attribute Does Not Exist",
    description: "A referenced attribute does not exist.",
    type: "uncertain",
  },
  FilterExceeded: {
    id: "FilterExceeded",
    name: "Filter Exceeded",
    description: "Filtering constraints were exceeded while evaluating candidates.",
    type: "uncertain",
  },
  InferredParentMissing: {
    id: "InferredParentMissing",
    name: "Inferred Parent Missing",
    description: "A required parent entity for inference was missing.",
    type: "uncertain",
  },
  InvalidCondition: {
    id: "InvalidCondition",
    name: "Invalid Condition",
    description: "A condition expression was invalid.",
    type: "uncertain",
  },
  InvalidTemporalRange: {
    id: "InvalidTemporalRange",
    name: "Invalid Temporal Range",
    description: "A temporal range was invalid (for example, start/end ordering).",
    type: "uncertain",
  },
  ExternalDataCallFailed: {
    id: "ExternalDataCallFailed",
    name: "External Data Call Failed",
    description: "A lookup/integration call to external data failed.",
    type: "uncertain",
  },
  InvalidDeltaFormat: {
    id: "InvalidDeltaFormat",
    name: "Invalid Delta Format",
    description: "A delta value or delta format was invalid.",
    type: "uncertain",
  },
  UncertainInstanceCount: {
    id: "UncertainInstanceCount",
    name: "Uncertain Instance Count",
    description: "The inferred relationship was resolved to uncertain.",
    type: "uncertain",
  },
  InvalidTemporalStartAttribute: {
    id: "InvalidTemporalStartAttribute",
    name: "Invalid Temporal Start Attribute",
    description: "The temporal start attribute was invalid or missing.",
    type: "uncertain",
  },
  InvalidTemporalEndAttribute: {
    id: "InvalidTemporalEndAttribute",
    name: "Invalid Temporal End Attribute",
    description: "The temporal end attribute was invalid or missing.",
    type: "uncertain",
  },
  InvalidGlobal: {
    id: "InvalidGlobal",
    name: "Invalid Global",
    description: "A global-only operation was used in an invalid context.",
    type: "uncertain",
  },
  InvalidRelationship: {
    id: "InvalidRelationship",
    name: "Invalid Relationship",
    description: "The relationship configuration or usage was invalid.",
    type: "uncertain",
  },
  NoTargetRelationship: {
    id: "NoTargetRelationship",
    name: "No Target Relationship",
    description: "A target relationship was required but missing.",
    type: "uncertain",
  },
  NoSourceRelationship: {
    id: "NoSourceRelationship",
    name: "No Source Relationship",
    description: "A source relationship was required but missing.",
    type: "uncertain",
  },
  NoParentEntities: {
    id: "NoParentEntities",
    name: "No Parent Entities",
    description: "No parent entities were available for this relationship operation.",
    type: "uncertain",
  },
  ExceededMaxInferred: {
    id: "ExceededMaxInferred",
    name: "Exceeded Max Inferred",
    description: "Inference exceeded the configured maximum number of generated instances.",
    type: "uncertain",
  },
  DivideByZero: {
    id: "DivideByZero",
    name: "Divide By Zero",
    description: "A division by zero was attempted.",
    type: "uncertain",
  },
  MissingAttributeId: {
    id: "MissingAttributeId",
    name: "Missing Attribute Id",
    description: "An element expected an attribute id but none was provided.",
    type: "uncertain",
  },
  RelationshipMissing: {
    id: "RelationshipMissing",
    name: "Relationship Missing",
    description: "A relationship was not found in the release.",
    type: "uncertain",
  },
  RelationshipNotFound: {
    id: "RelationshipNotFound",
    name: "Relationship Not Found",
    description: "A relationship id/name could not be found in the release graph.",
    type: "uncertain",
  },
  ExpectedNamedRelationship: {
    id: "ExpectedNamedRelationship",
    name: "Expected Named Relationship",
    description: "A named relationship was expected but not provided.",
    type: "uncertain",
  },
  RelationshipUncertain: {
    id: "RelationshipUncertain",
    name: "Relationship Uncertain",
    description: "The relationship was uncertain.",
    type: "uncertain",
  },
};
